import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

const OCCASIONS = [
  "Wedding / Anniversary",
  "Birthday",
  "Baby Shower / Newborn",
  "Diwali / Festive",
  "Housewarming",
  "Corporate Gifting",
  "Just because",
];

const BUDGETS = [
  "Under ₹500",
  "₹500 – ₹1,500",
  "₹1,500 – ₹3,000",
  "₹3,000 – ₹5,000",
  "₹5,000+",
];

const EMPTY = {
  name: "", email: "", phone: "",
  occasion: "", budget: "",
  color_preferences: "", description: "",
  reference_image: "",
};

export default function CustomOrderSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please tell me your name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email");
    if (form.description.trim().length < 10) return toast.error("Please describe your request in a bit more detail");
    setSubmitting(true);
    try {
      await api.post("/custom-orders", form);
      setSuccess(true);
      setForm(EMPTY);
      toast.success("Request sent! I'll reach out within 24–48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-rani/5 py-20 relative overflow-hidden" data-testid="custom-order-section">
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-marigold/20 blur-3xl" aria-hidden />
      <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-rani/10 blur-3xl" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
        {/* Pitch */}
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-marigold/30 text-deepbrown text-xs font-bold uppercase tracking-widest">
            <Sparkles className="h-3 w-3 text-rani" /> Made just for you
          </span>
          <h2 className="font-display font-bold text-deepbrown text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            Got something <span className="text-rani">special</span> in mind?
          </h2>
          <p className="mt-5 text-deepbrown/75 leading-relaxed">
            Tell me what you're dreaming of — a custom bouquet for a wedding, a name-themed amigurumi
            for a newborn, a colour-matched curtain tieback set, or something completely your own.
            I'll personally read every request and reply within 24–48 hours with a sketch, materials
            and a fair quote.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-deepbrown/80">
            <li className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-emerald mt-0.5 shrink-0" /> Hand-made by me, one stitch at a time</li>
            <li className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-emerald mt-0.5 shrink-0" /> Free conversations, no obligation to buy</li>
            <li className="flex gap-2 items-start"><CheckCircle2 className="h-4 w-4 text-emerald mt-0.5 shrink-0" /> Turnaround typically 5–14 days, festive seasons may be longer</li>
          </ul>
        </div>

        {/* Form / Success */}
        <div className="lg:col-span-7">
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-deepbrown/10">
            {success ? (
              <div className="text-center py-10" data-testid="custom-order-success">
                <div className="h-16 w-16 rounded-full bg-emerald grid place-items-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-display text-2xl text-deepbrown mt-4 font-bold">Request received 🧶</h3>
                <p className="text-deepbrown/70 mt-2 max-w-md mx-auto">
                  Thank you! I've sent a confirmation to your inbox and will personally reply with
                  ideas and a quote within 24–48 hours.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-6 px-6 py-2.5 rounded-full bg-deepbrown text-ivory text-sm font-bold hover:bg-rani"
                  data-testid="custom-order-new"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="custom-order-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Your name *</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Priya Sharma" data-testid="co-name" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="co-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Phone (optional)</Label>
                    <Input value={form.phone} maxLength={10} onChange={(e) => set("phone", e.target.value)} placeholder="98XXXXXXXX" data-testid="co-phone" />
                  </div>
                  <div>
                    <Label>Occasion</Label>
                    <Select value={form.occasion} onValueChange={(v) => set("occasion", v)}>
                      <SelectTrigger data-testid="co-occasion"><SelectValue placeholder="Pick one" /></SelectTrigger>
                      <SelectContent>
                        {OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Budget</Label>
                    <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                      <SelectTrigger data-testid="co-budget"><SelectValue placeholder="Pick one" /></SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Colour or style preferences (optional)</Label>
                  <Input value={form.color_preferences} onChange={(e) => set("color_preferences", e.target.value)} placeholder="Pastel pinks, marigold yellow, boho..." data-testid="co-colors" />
                </div>
                <div>
                  <Label>Describe your dream piece *</Label>
                  <Textarea
                    rows={5}
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="A bouquet of 12 pastel-pink tulips for my mom's 60th birthday on 15 March. She loves soft colours and we'd like a small handwritten card included."
                    data-testid="co-description"
                  />
                </div>
                <div>
                  <Label>Reference image URL (optional)</Label>
                  <Input
                    value={form.reference_image}
                    onChange={(e) => set("reference_image", e.target.value)}
                    placeholder="Paste an inspiration link from Pinterest, Instagram..."
                    data-testid="co-reference"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-rani text-white font-bold shadow-lg hover:scale-[1.02] transition-transform disabled:opacity-50"
                  data-testid="co-submit"
                >
                  {submitting ? "Sending…" : (<>Send Request <Sparkles className="h-4 w-4" /></>)}
                </button>
                <p className="text-[11px] text-deepbrown/50">
                  Your details are only used to reply about this request. No marketing, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
